import { sessionHeaders } from '@/lib/api/client';
import { useAuthStore } from '@/store/useAuthStore';

/**
 * Client error reporting to sreg-api `PUT /log` (signed-in only; sreg-api takes
 * the user from the session and caps sizes). Lines land in Cloud Logging as
 * component "sreg-app" with the failing request's correlationId, so one id
 * follows a problem across the SPA, the BFF and sreg-api.
 *
 * Reported: uncaught errors, unhandled promise rejections, render crashes,
 * failed API/BFF calls, chat stream failures. Rate-limited so a broken page
 * can't flood it: at most MAX_PER_MINUTE lines, and the same message at most
 * once per DEDUPE_MS. Never throws, never reports its own failures.
 */

export type ClientLogLevel = 'ERROR' | 'WARN';

export interface ClientLogLine {
  source: string; // e.g. "window.error", "api", "chat.stream", "render"
  level: ClientLogLevel;
  message: string;
  correlationId?: string;
  error?: unknown;
  data?: Record<string, unknown>;
}

const MAX_PER_MINUTE = 10;
const DEDUPE_MS = 30_000;
const sentAt: number[] = [];
const lastByKey = new Map<string, number>();

export function newCorrelationId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
}

function allowed(key: string, now: number): boolean {
  while (sentAt.length && now - sentAt[0] > 60_000) sentAt.shift();
  if (sentAt.length >= MAX_PER_MINUTE) return false;
  const last = lastByKey.get(key);
  if (last !== undefined && now - last < DEDUPE_MS) return false;
  sentAt.push(now);
  lastByKey.set(key, now);
  return true;
}

function errorInfo(err: unknown): { name: string; message: string; stack: string } | undefined {
  if (!err) return undefined;
  if (err instanceof Error) return { name: err.name, message: err.message, stack: err.stack ?? '' };
  return { name: 'NonError', message: String(err), stack: '' };
}

export function reportClient(line: ClientLogLine): void {
  try {
    // /log needs a signed-in, device-verified session.
    if (useAuthStore.getState().sessionStatus !== 'ready') return;
    const now = Date.now();
    if (!allowed(`${line.source}|${line.message}`, now)) return;
    const body = {
      source: line.source,
      level: line.level,
      message: line.message.slice(0, 2000),
      correlationId: line.correlationId,
      error: errorInfo(line.error),
      data: { page: window.location.pathname, userAgent: navigator.userAgent, ...line.data },
    };
    void fetch('/api/log', {
      method: 'PUT',
      credentials: 'include',
      keepalive: true,
      headers: { ...sessionHeaders(true), ...(line.correlationId ? { CorrelationID: line.correlationId } : {}) },
      body: JSON.stringify(body),
    }).catch(() => undefined);
  } catch {
    /* reporting must never break the app */
  }
}

/** Report a failed API/BFF call (4xx as WARN, 5xx and network failures as ERROR). */
export function reportRequestFailure(f: {
  method: string;
  path: string;
  status?: number;
  correlationId?: string;
  error?: unknown;
}): void {
  if (f.path.startsWith('/api/log')) return;
  if (f.status === 401) return; // session expiry: handled by refresh / sign-in, not an error
  const level: ClientLogLevel = f.status !== undefined && f.status < 500 ? 'WARN' : 'ERROR';
  const what = f.status !== undefined ? `${f.status}` : 'network error';
  reportClient({
    source: f.path.startsWith('/bff') ? 'bff' : 'api',
    level,
    message: `${f.method} ${f.path.split('?')[0]} failed: ${what}`,
    correlationId: f.correlationId,
    error: f.error,
    data: { method: f.method, path: f.path.split('?')[0], status: f.status },
  });
}

let installed = false;

/** Uncaught errors and unhandled promise rejections. Idempotent. */
export function installGlobalErrorReporting(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('error', (e) => {
    reportClient({ source: 'window.error', level: 'ERROR', message: e.message || 'Uncaught error', error: e.error });
  });
  window.addEventListener('unhandledrejection', (e) => {
    const reason = e.reason;
    reportClient({
      source: 'unhandledrejection',
      level: 'ERROR',
      message: reason instanceof Error ? reason.message : String(reason),
      error: reason,
    });
  });
}

/** Test hook. */
export function _resetClientLogForTest(): void {
  sentAt.length = 0;
  lastByKey.clear();
}
